import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import React from "react";
import { useSearchParams } from "react-router-dom";

import {
  DEFAULT_EXPIRY_RISK_PCT,
  EXPIRY_RISK_QUERY,
  type ExpiryRiskPct,
  getExpiryRisk,
  getWarehouseDashboardSummary,
  getWarehouseOperations,
  getWarehouseShipments,
  OPERATIONS_WEEKS,
  STOCK_ITEMS_QUERY,
} from "@/api/warehouse_dashboard";
import { getWarehouseTurnoverSummary, type TurnoverDays } from "@/api/warehouse_turnover";
import { Button } from "@/components/ui/button";
import { fmtTime } from "@/lib/format";
import { LoadState } from "@/lib/scan-ui/screen";
import { cn } from "@/lib/utils";
import { earliestAsOf } from "@/lib/warehouse-overview";

import { ExpiryRiskBlock } from "./ExpiryRiskBlock";
import { OperationsTab } from "./OperationsTab";
import { OVERVIEW_TURNOVER_DAYS, OverviewTab } from "./OverviewTab";
import { ShelfLifeTab } from "./ShelfLifeTab";
import { StockTab } from "./StockTab";
import {
  DASHBOARD_TABS,
  DASHBOARD_STALE_MS,
  type DashboardTab,
  type DashboardTabSource,
  TAB_PARAM,
  tabFromParam,
  tabSources,
} from "./tabs";
import { INITIAL_TURNOVER_DAYS, TurnoverTab, turnoverSubtitle } from "./TurnoverTab";

/**
 * Дашборд склада — страница с вкладками (карта `tabs.ts`, активная — в
 * `?tab=`). Шапка общая: заголовок, время среза и «Обновить» ОТКРЫТОЙ
 * вкладки. Каждый запрос включён (`enabled`) только пока открыта вкладка,
 * которой он нужен (`tabs.ts::sources`): «Остатки» и «Сроки годности» — один
 * ответ `/summary`, «Оборачиваемость» — свой, с периодом в ключе, «Операции» —
 * `/operations` (БД портала, не 1С), «Обзор» — все четыре плюс `/shipments`
 * параллельно, с теми же ключами (кеш общий с остальными вкладками).
 */
export function WarehouseDashboardPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const tab = tabFromParam(params.get(TAB_PARAM));
  const sources = tabSources(tab);
  const needs = (s: DashboardTabSource) => sources.includes(s);
  const overview = tab === "overview";

  const summaryQ = useQuery({
    queryKey: ["warehouse-dashboard-summary"],
    queryFn: getWarehouseDashboardSummary,
    staleTime: DASHBOARD_STALE_MS,
    enabled: needs("summary"),
  });

  // Период выбирают на вкладке «Оборачиваемость»; «Обзор» всегда смотрит 90
  // дней. Ключ один и тот же при том же периоде — ответ переиспользуется.
  const [days, setDays] = React.useState<TurnoverDays>(INITIAL_TURNOVER_DAYS);
  const turnoverDays = overview ? OVERVIEW_TURNOVER_DAYS : days;
  const turnoverQ = useQuery({
    queryKey: ["warehouse-turnover-summary", turnoverDays],
    queryFn: () => getWarehouseTurnoverSummary(turnoverDays),
    staleTime: DASHBOARD_STALE_MS,
    enabled: needs("turnover"),
  });

  // Не 1С, а БД портала — дёшево, но картина та же: время расчёта рядом с
  // «Обновить», перечитывание — кнопкой.
  const operationsQ = useQuery({
    queryKey: ["warehouse-dashboard-operations", OPERATIONS_WEEKS],
    queryFn: () => getWarehouseOperations(OPERATIONS_WEEKS),
    staleTime: DASHBOARD_STALE_MS,
    enabled: needs("operations"),
  });

  // Отгрузка по расходным из 1С — только для плитки «Обзора».
  const shipmentsQ = useQuery({
    queryKey: ["warehouse-dashboard-shipments", OPERATIONS_WEEKS],
    queryFn: () => getWarehouseShipments(OPERATIONS_WEEKS),
    staleTime: DASHBOARD_STALE_MS,
    enabled: needs("shipments"),
  });

  // «Просрочка и риск» — верх «Сроков годности»; порог выбирают там же, он в
  // ключе: у каждого порога свой ответ.
  const [riskPct, setRiskPct] = React.useState<ExpiryRiskPct>(DEFAULT_EXPIRY_RISK_PCT);
  const expiryQ = useQuery({
    queryKey: [EXPIRY_RISK_QUERY, riskPct],
    queryFn: () => getExpiryRisk(riskPct),
    staleTime: DASHBOARD_STALE_MS,
    enabled: needs("expiry_risk"),
  });

  const all = [summaryQ, turnoverQ, operationsQ, shipmentsQ];
  const active =
    tab === "turnover" ? turnoverQ : tab === "operations" ? operationsQ : summaryQ;
  const queryClient = useQueryClient();
  const refresh = () => {
    if (overview) all.forEach((x) => void x.refetch());
    else void active.refetch();
    if (tab === "shelf-life") void expiryQ.refetch();
    // Раскрытые склады «Остатков» — часть той же картинки: без этого их итог
    // остался бы от прошлого среза и разошёлся со свежей строкой склада.
    if (tab === "stock") void queryClient.invalidateQueries({ queryKey: [STOCK_ITEMS_QUERY] });
  };
  const fetching = overview
    ? all.some((x) => x.isFetching)
    : active.isFetching || (tab === "shelf-life" && expiryQ.isFetching);

  // Подпись — к тому, что НА ЭКРАНЕ: при ошибке там `LoadState`, и «Данные на
  // 07:30» от прошлой удачной загрузки врал бы про картинку, которой нет. Не
  // разобрался `as_of` — показываем как есть, это видно. «Обзор» — самый
  // РАННИЙ срез из четырёх и только когда ответили все (`earliestAsOf`).
  const overviewAsOf = overview
    ? earliestAsOf(all.map((x) => (x.data && !x.error ? x.data.as_of : null)))
    : null;
  const subtitle = overview
    ? overviewAsOf
      ? `1С и портал · данные на ${fmtTime(overviewAsOf) || overviewAsOf}`
      : "1С и портал"
    : tab === "turnover"
      ? turnoverSubtitle(turnoverQ)
      : tab === "operations"
        ? operationsQ.data && !operationsQ.error
          ? `Работа через портал · данные на ${fmtTime(operationsQ.data.as_of) || operationsQ.data.as_of}`
          : "Работа через портал"
        : summaryQ.data && !summaryQ.error
          ? `Данные на ${fmtTime(summaryQ.data.as_of) || summaryQ.data.as_of}`
          : "Живой остаток из 1С";

  const selectTab = (next: DashboardTab) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.set(TAB_PARAM, next);
        return out;
      },
      { replace: true },
    );

  return (
    <div className="mx-auto max-w-[1400px] space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold">Дашборд склада</h1>
          <p className="break-words text-[13px] text-muted-foreground">{subtitle}</p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh}>
          <RefreshCw className={cn("mr-2 size-4", fetching && "animate-spin")} />
          Обновить
        </Button>
      </div>

      <div role="tablist" aria-label="Разделы дашборда" className="flex gap-1 overflow-x-auto border-b">
        {DASHBOARD_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={t.key === tab}
            onClick={() => selectTab(t.key)}
            className={cn(
              "-mb-px whitespace-nowrap border-b-2 px-3 py-1.5 text-[13px] font-medium",
              t.key === tab
                ? "border-foreground text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.title}
          </button>
        ))}
      </div>

      <div role="tabpanel">
        {overview ? (
          <OverviewTab
            q={{
              summary: summaryQ,
              turnover: turnoverQ,
              operations: operationsQ,
              shipments: shipmentsQ,
            }}
          />
        ) : tab === "turnover" ? (
          <TurnoverTab q={turnoverQ} days={days} onDaysChange={setDays} />
        ) : tab === "shelf-life" ? (
          // Блок риска — свой запрос и своя ошибка: упавшая сводка не прячет
          // список просрочки, и наоборот.
          <div className="space-y-3">
            <ExpiryRiskBlock q={expiryQ} pct={riskPct} onPctChange={setRiskPct} />
            {summaryQ.isLoading || summaryQ.error ? (
              <LoadState
                isLoading={summaryQ.isLoading}
                error={summaryQ.error}
                onRetry={() => void summaryQ.refetch()}
                className="flex flex-col items-center justify-center gap-3 p-8 text-center"
              />
            ) : (
              summaryQ.data && <ShelfLifeTab data={summaryQ.data} />
            )}
          </div>
        ) : active.isLoading || active.error ? (
          <LoadState
            isLoading={active.isLoading}
            error={active.error}
            onRetry={() => void active.refetch()}
            className="flex flex-col items-center justify-center gap-3 p-8 text-center"
          />
        ) : tab === "operations" ? (
          operationsQ.data && <OperationsTab data={operationsQ.data} />
        ) : (
          summaryQ.data && <StockTab data={summaryQ.data} />
        )}
      </div>
    </div>
  );
}

export default WarehouseDashboardPage;
